import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import { useTranslation } from 'react-i18next'

import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { PageMeta } from '../../components/common/PageMeta'
import { useBreadcrumbTitle } from '../../components/layout/Breadcrumbs'
import { PhotoStrip } from '../../components/content/PhotoStrip'
import { Lightbox } from '../../components/common/Lightbox'
import { ErrorState } from '../../components/common/States'
import { AvailabilityCalendar, type DateRange } from '../../components/calendar/AvailabilityCalendar'
import { BookingPanel } from '../booking/BookingPanel'
import { unitsApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { useFormatters } from '../../hooks/useFormatters'
import { NotFoundPage } from '../../components/common/NotFoundPage'
import { ApiError } from '../../api/client'

/** ISO date, local time. */
function toIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

export function HouseDetailPage() {
  const { slug = '' } = useParams()
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { money } = useFormatters()
  const [range, setRange] = useState<DateRange>({ checkIn: null, checkOut: null })

  const unit = useQuery({
    queryKey: ['unit', slug, locale],
    queryFn: () => unitsApi.get(slug, locale),
  })

  // A rolling twelve-month window, which is as far ahead as anyone books a cottage and
  // comfortably inside the API's 400-night cap.
  const { from, to } = useMemo(() => {
    const start = new Date()
    const end = new Date()
    end.setMonth(end.getMonth() + 12)
    return { from: toIso(start), to: toIso(end) }
  }, [])

  const availability = useQuery({
    queryKey: ['availability', slug, from, to],
    queryFn: () => unitsApi.availability(slug, from, to),
    // Availability is the one thing that must never be served from cache: a stale calendar
    // shows a sold night as free and creates a booking the owner has to apologise for.
    staleTime: 0,
    gcTime: 60 * 1000,
    refetchOnMount: 'always',
  })

  // Above the early returns: a hook cannot sit after a conditional return, and the trail
  // needs the real name - "Päätalo", not the slug.
  useBreadcrumbTitle(unit.data?.name)

  if (unit.isError && unit.error instanceof ApiError && unit.error.status === 404) {
    return <NotFoundPage />
  }

  if (unit.isPending) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography color="text.secondary">{t('common.loading')}…</Typography>
      </Container>
    )
  }

  if (unit.isError || !unit.data) {
    return (
      <Container sx={{ py: 8 }}>
        <ErrorState error={unit.error} onRetry={() => void unit.refetch()} />
      </Container>
    )
  }

  const house = unit.data
  // The hero and the social-preview tag both render an <img>, so the fallback has to skip
  // films. The database forbids a film being flagged as cover; this covers the case where
  // nothing is flagged at all and the first item happens to be one.
  const stills = house.images.filter((image) => image.kind !== 'VIDEO')
  const cover = stills.find((image) => image.cover) ?? stills[0]

  return (
    <>
      <PageMeta
        title={house.name}
        description={house.summary ?? undefined}
        image={cover?.url}
        path={`/houses/${house.slug}`}
      />

      {/* ---------------------------------------------------------------- header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Container sx={{ py: { xs: 4, md: 6 } }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={house.unitType === 'MAIN_HOUSE' ? t('houses.mainHouse') : t('houses.lakeHouse')}
            sx={{ mb: 1.5 }}
          />
          <Typography variant="h1" sx={{ mb: 1.5 }}>
            {house.name}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 720, mb: 2.5 }}>
            {house.summary}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Fact icon="👥" label={t('houses.sleeps', { count: house.maxGuests })} />
            <Fact icon="🛏️" label={t('houses.bedrooms', { count: house.bedrooms })} />
            <Fact icon="🛁" label={t('houses.bathrooms', { count: house.bathrooms })} />
            {house.sizeSqm && <Fact icon="📐" label={t('houses.size', { count: house.sizeSqm })} />}
            <Fact icon="🌙" label={t('houses.minStay', { count: house.minNights })} />
          </Stack>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 5, md: 7 } }}>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 4, lg: 6 },
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
            alignItems: 'start',
          }}
        >
          {/* ------------------------------------------------------------ main */}
          <Box sx={{ minWidth: 0 }}>
            <Gallery images={house.images} emptyLabel={t('houses.noImages')} title={t('houses.gallery')} />

            <Typography variant="h3" sx={{ mt: 5, mb: 2 }}>
              {t('houses.aboutThis')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {house.description}
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('houses.amenities')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1.25,
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              }}
            >
              {house.amenities.map((amenity) => (
                <Stack key={amenity.id} direction="row" spacing={1} alignItems="center">
                  <Box aria-hidden sx={{ fontSize: '1.15rem', lineHeight: 1 }}>
                    {amenity.icon ?? '•'}
                  </Box>
                  <Typography variant="body2">{amenity.label}</Typography>
                </Stack>
              ))}
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('calendar.title')}
            </Typography>

            {availability.isError ? (
              <ErrorState error={availability.error} onRetry={() => void availability.refetch()} />
            ) : (
              <AvailabilityCalendar
                days={availability.data?.days ?? []}
                value={range}
                onChange={setRange}
                loading={availability.isFetching}
              />
            )}

            {house.latitude && house.longitude && (
              <>
                <Divider sx={{ my: 4 }} />
                <Typography variant="h3" sx={{ mb: 1.5 }}>
                  {t('houses.location')}
                </Typography>
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${house.latitude},${house.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📍 {t('contact.openInMaps')}
                </Link>
              </>
            )}
          </Box>

          {/* ------------------------------------------------------------ booking */}
          <Paper
            elevation={0}
            sx={{
              position: { lg: 'sticky' },
              top: { lg: 96 },
              p: 3,
              border: 1,
              borderColor: 'divider',
              borderRadius: 4,
            }}
          >
            <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 2 }}>
              <Typography variant="h3" component="p" color="primary.main">
                {money(house.basePricePerNight)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('common.perNight')}
              </Typography>
            </Stack>

            <BookingPanel unit={house} range={range} />
          </Paper>
        </Box>
      </Container>

      {/* The estate, not this cottage.
          Titled so nobody reads it as "pictures of the building you are booking" - two of the
          five have only one or two photographs of their own, and an empty page sells nothing,
          but a picture of the wrong cottage is worse than an empty page. */}
      <Container sx={{ pb: { xs: 8, md: 12 } }}>
        <Typography variant="h2" sx={{ mb: 1 }}>
          {t('houses.moreFromTheEstate')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('houses.moreFromTheEstateBody')}
        </Typography>
        <Stack spacing={4}>
          <PhotoStrip category="HOUSE" count={6} />
          <PhotoStrip category="SAUNA" count={6} showAllLink={false} />
          <PhotoStrip category="NATURE" count={6} showAllLink={false} />
          <PhotoStrip kind="VIDEO" count={6} title={t('gallery.films')} showAllLink={false} />
        </Stack>
      </Container>
    </>
  )
}

function Fact({ icon, label }: { icon: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box aria-hidden sx={{ fontSize: '1.05rem', lineHeight: 1 }}>
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}

/** How many of a cottage's photographs are shown before the visitor asks for the rest. */
const GALLERY_PREVIEW = 5

function Gallery({
  images,
  emptyLabel,
  title,
}: {
  images: { id: string; url: string; altText?: string | null; kind?: 'IMAGE' | 'VIDEO'; posterUrl?: string | null }[]
  emptyLabel: string
  title: string
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  if (images.length === 0) {
    return (
      <Box
        sx={{
          aspectRatio: '16 / 9',
          borderRadius: 4,
          bgcolor: 'primary.light',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.contrastText',
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <Box aria-hidden sx={{ fontSize: '3rem' }}>
            🏞️
          </Box>
          <Typography variant="body2">{emptyLabel}</Typography>
        </Stack>
      </Box>
    )
  }

  // Five is a composition - one wide, four beneath it - not a limit on what exists. The main
  // house has twenty-eight photographs and used to show five of them with nothing to say the
  // other twenty-three were there.
  const shown = expanded ? images : images.slice(0, GALLERY_PREVIEW)
  const move = (delta: number) =>
    setOpenIndex((current) => {
      if (current === null) return current
      const next = current + delta
      return next < 0 || next >= images.length ? current : next
    })

  return (
    <>
      <Box
        role="group"
        aria-label={title}
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          '& > :first-of-type': { gridColumn: { sm: '1 / -1' } },
        }}
      >
        {shown.map((image, index) => (
          <Box
            key={image.id}
            onClick={() => setOpenIndex(index)}
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: index === 0 ? '16 / 9' : '4 / 3',
              borderRadius: 3,
              overflow: 'hidden',
              cursor: 'zoom-in',
              bgcolor: 'action.hover',
            }}
          >
            {image.kind === 'VIDEO' ? (
              <>
                <Box
                  component="video"
                  src={image.url}
                  muted
                  playsInline
                  // 20 to 60 MB each. Nothing is fetched until somebody opens one.
                  preload="none"
                  // The library's poster for the same file; black box only if it has none yet.
                  poster={image.posterUrl ?? undefined}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', bgcolor: 'grey.900' }}
                />
                <PlayCircleIcon
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 52,
                    color: 'common.white',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                    pointerEvents: 'none',
                  }}
                />
              </>
            ) : (
              <Box
                component="img"
                src={image.url}
                alt={image.altText ?? ''}
                loading={index === 0 ? 'eager' : 'lazy'}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            )}
          </Box>
        ))}
      </Box>

      {images.length > GALLERY_PREVIEW && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={() => setExpanded((open) => !open)}>
            {expanded
              ? t('houses.showFewerPhotos')
              : t('houses.showAllPhotos', { count: images.length })}
          </Button>
        </Box>
      )}

      <Lightbox items={images} index={openIndex} onClose={() => setOpenIndex(null)} onMove={move} />
    </>
  )
}
